"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect, useRef, useState } from "react";

import { fetchJson } from "@/lib/api";

function useOnClickOutside(ref: React.RefObject<HTMLElement | null>, onClose: () => void) {
  useEffect(() => {
    function onDoc(e: MouseEvent) {
      const el = ref.current;
      if (!el) return;
      if (e.target instanceof Node && el.contains(e.target)) return;
      onClose();
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [ref, onClose]);
}

export function UserMenu() {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useOnClickOutside(ref, () => setOpen(false));
  const [myTodoKey, setMyTodoKey] = useState<string>("todo-member-kowaka");
  const [myPageKey, setMyPageKey] = useState<string>("member-kowaka");
  const [myName, setMyName] = useState<string>("");
  const router = useRouter();

  useEffect(() => {
    (async () => {
      try {
        const status = await fetchJson<{ authenticated: boolean; session: { member_global_id: string; member_name: string } | null }>(
          "/api/auth/status",
          { cache: "no-store" },
        );
        const gid = status.session?.member_global_id ?? "";
        const name = status.session?.member_name ?? "";
        setMyName(name);
        const memberKey = gid.replace(/^member_/, "member-");
        setMyPageKey(memberKey);
        setMyTodoKey(`todo-${memberKey}`);
      } catch {
        // keep defaults
      }
    })();
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        aria-label="ユーザーメニュー"
        onClick={() => setOpen((v) => !v)}
        className="flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
      >
        <svg viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
          <path
            fill="currentColor"
            d="M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4Zm0 2c-4.42 0-8 2-8 4.5V20h16v-1.5C20 16 16.42 14 12 14Z"
          />
        </svg>
      </button>

      {open ? (
        <div className="absolute right-0 mt-2 w-56 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg">
          {myName ? <div className="px-4 py-3 text-xs text-slate-500">ログイン中: {myName}</div> : null}
          <Link
            href={`/docs/browse/${myTodoKey}`}
            className="block px-4 py-3 text-sm hover:bg-slate-50"
            onClick={(e) => {
              e.preventDefault();
              setOpen(false);
              void (async () => {
                try {
                  await fetchJson<{ ok: true }>(`/api/docs/ensure`, { method: "POST", body: JSON.stringify({ stable_key: myTodoKey }) });
                } catch {
                  // ignore
                }
                router.push(`/docs/browse/${myTodoKey}`);
              })();
            }}
          >
            自分のTODOを開く
          </Link>
          <Link
            href={`/docs/browse/${myPageKey}`}
            className="block px-4 py-3 text-sm hover:bg-slate-50"
            onClick={(e) => {
              e.preventDefault();
              setOpen(false);
              void (async () => {
                try {
                  await fetchJson<{ ok: true }>(`/api/docs/ensure`, { method: "POST", body: JSON.stringify({ stable_key: myPageKey }) });
                } catch {
                  // ignore
                }
                router.push(`/docs/browse/${myPageKey}`);
              })();
            }}
          >
            自分の個人ページを開く
          </Link>
          <div className="h-px bg-slate-100" />
          <Link
            href="/settings"
            className="block px-4 py-3 text-sm hover:bg-slate-50"
            onClick={() => setOpen(false)}
          >
            設定
          </Link>
          <div className="h-px bg-slate-100" />
          <button
            type="button"
            className="block w-full px-4 py-3 text-left text-sm hover:bg-slate-50"
            onClick={() => {
              setOpen(false);
              void (async () => {
                try {
                  await fetchJson<{ ok: true }>("/api/auth/logout", { method: "POST", body: JSON.stringify({}) });
                } catch {
                  // ignore
                }
                window.location.href = "/login";
              })();
            }}
          >
            ログアウト
          </button>
        </div>
      ) : null}
    </div>
  );
}
